import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./App.css";

const API = "https://api.idekod.com.tr";

// Gün türü seçenekleri
const TYPE_OPTIONS = [
  { value: "calisma", label: "Çalışma Günü" },
  { value: "yarim", label: "Yarım Gün Tatil" },
  { value: "tam", label: "Tam Gün Tatil" },
];

const TYPE_LABEL = {
  calisma: "Çalışma Günü",
  yarim: "Yarım Gün Tatil",
  tam: "Tam Gün Tatil",
};

// Hafta sonu değeri ENUM eşlemesi: /api/settings tablosu eski 'none' değerini kullanıyor.
// 'calisma' -> 'none' diye kaydedip geri okurken 'none' -> 'calisma' yaparız (ENUM güvenli).
const TO_LEGACY = { calisma: "none", yarim: "yarim", tam: "tam" };
const FROM_LEGACY = { none: "calisma", yarim: "yarim", tam: "tam" };

const CATEGORY_LABEL = {
  normal: "Normal Gün",
  haftasonu: "Hafta Sonu",
  ozel: "Kurum Özel Tatili",
  resmi: "Resmi Tatil",
  dini: "Dini Bayram",
};

const GUN_ADLARI = [
  "Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi",
];
const HAFTA_BASLIK = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const AY_ADLARI = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

// Yerel saate göre YYYY-MM-DD üretir (toISOString UTC kaymasını önler)
const isoOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

// Türkiye resmi (milli) tatilleri – sabit gregoryen tarihler
const resmiTatiller = (year) => [
  { tarih: `${year}-01-01`, name: "Yılbaşı" },
  { tarih: `${year}-04-23`, name: "Ulusal Egemenlik ve Çocuk Bayramı" },
  { tarih: `${year}-05-01`, name: "Emek ve Dayanışma Günü" },
  { tarih: `${year}-05-19`, name: "Atatürk'ü Anma, Gençlik ve Spor Bayramı" },
  { tarih: `${year}-07-15`, name: "Demokrasi ve Millî Birlik Günü" },
  { tarih: `${year}-08-30`, name: "Zafer Bayramı" },
  { tarih: `${year}-10-29`, name: "Cumhuriyet Bayramı" },
];

// Dini bayramlar (Diyanet tahmini) – aya göre kaydığı için yıl bazlı tablo.
// Tarihler tahminîdir; gerekirse kullanıcı takvimden düzenleyebilir.
const DINI_BAYRAM_TABLOSU = {
  2024: {
    ramazan: ["2024-04-10", "2024-04-11", "2024-04-12"], ramazanArife: "2024-04-09",
    kurban: ["2024-06-16", "2024-06-17", "2024-06-18", "2024-06-19"], kurbanArife: "2024-06-15",
  },
  2025: {
    ramazan: ["2025-03-30", "2025-03-31", "2025-04-01"], ramazanArife: "2025-03-29",
    kurban: ["2025-06-06", "2025-06-07", "2025-06-08", "2025-06-09"], kurbanArife: "2025-06-05",
  },
  2026: {
    ramazan: ["2026-03-20", "2026-03-21", "2026-03-22"], ramazanArife: "2026-03-19",
    kurban: ["2026-05-27", "2026-05-28", "2026-05-29", "2026-05-30"], kurbanArife: "2026-05-26",
  },
  2027: {
    ramazan: ["2027-03-10", "2027-03-11", "2027-03-12"], ramazanArife: "2027-03-09",
    kurban: ["2027-05-16", "2027-05-17", "2027-05-18", "2027-05-19"], kurbanArife: "2027-05-15",
  },
  2028: {
    ramazan: ["2028-02-26", "2028-02-27", "2028-02-28"], ramazanArife: "2028-02-25",
    kurban: ["2028-05-05", "2028-05-06", "2028-05-07", "2028-05-08"], kurbanArife: "2028-05-04",
  },
};

const diniBayramlar = (year) => {
  const t = DINI_BAYRAM_TABLOSU[year];
  if (!t) return [];
  const list = [];
  if (t.ramazanArife)
    list.push({ tarih: t.ramazanArife, name: "Ramazan Bayramı Arifesi", type: "yarim" });
  t.ramazan.forEach((d, i) =>
    list.push({ tarih: d, name: `Ramazan Bayramı ${i + 1}. Gün`, type: "tam" })
  );
  if (t.kurbanArife)
    list.push({ tarih: t.kurbanArife, name: "Kurban Bayramı Arifesi", type: "yarim" });
  t.kurban.forEach((d, i) =>
    list.push({ tarih: d, name: `Kurban Bayramı ${i + 1}. Gün`, type: "tam" })
  );
  return list;
};

const TarihSec = () => {
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState({
    year: today.getFullYear(),
    month: today.getMonth(),
  });

  // Hafta sonu ayarları
  const [weekend, setWeekend] = useState({ saturday: "tam", sunday: "tam" });
  const [isHolidayChecked, setIsHolidayChecked] = useState(false);

  // Özel günler: { 'YYYY-MM-DD': { type, category, name, description } }
  const [days, setDays] = useState({});

  // Seçili gün modalı
  const [selected, setSelected] = useState(null); // { iso, date }
  const [editType, setEditType] = useState("tam");
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editCategory, setEditCategory] = useState("ozel");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ---- Veri yükleme ----
  useEffect(() => {
    const load = async () => {
      try {
        const dRes = await fetch(`${API}/api/takvim-gunler`);
        const d = await dRes.json().catch(() => ({}));

        setWeekend({
          saturday: FROM_LEGACY[d?.saturdayType] || "tam",
          sunday: FROM_LEGACY[d?.sundayType] || "tam",
        });
        setIsHolidayChecked(!!d?.isHolidayChecked);

        const map = {};
        (d?.days || []).forEach((row) => {
          map[row.tarih] = {
            type: row.type || "tam",
            category: row.category || "ozel",
            name: row.name || "",
            description: row.description || "",
          };
        });
        setDays(map);
      } catch (err) {
        console.error("Takvim verileri alınamadı:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ---- Bir günün etkin durumunu çöz ----
  const resolveDay = useCallback(
    (date) => {
      const iso = isoOf(date);
      const override = days[iso];
      if (override) return { ...override, iso, isOverride: true };

      const dow = date.getDay();
      if (dow === 6)
        return { type: weekend.saturday, category: "haftasonu", name: "Cumartesi", description: "", iso, isOverride: false };
      if (dow === 0)
        return { type: weekend.sunday, category: "haftasonu", name: "Pazar", description: "", iso, isOverride: false };
      return { type: "calisma", category: "normal", name: "", description: "", iso, isOverride: false };
    },
    [days, weekend]
  );

  // ---- Hücre CSS sınıfı ----
  const cellClass = (info, isToday) => {
    let cls = "cal-cell";
    if (info.category === "resmi") cls += " cal-cell--resmi";
    else if (info.category === "dini") cls += " cal-cell--dini";
    else if (info.type === "tam") cls += " cal-cell--tam";
    else if (info.type === "yarim") cls += " cal-cell--yarim";
    else cls += " cal-cell--calisma";
    if (info.type === "yarim") cls += " is-half";
    if (info.isOverride && info.category === "ozel") cls += " is-ozel";
    if (isToday) cls += " is-today";
    return cls;
  };

  // ---- Ay ızgarası ----
  const monthGrid = useMemo(() => {
    const { year, month } = view;
    const totalDays = new Date(year, month + 1, 0).getDate();
    const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Pzt = 0
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [view]);

  const todayIso = isoOf(today);

  // ---- Ay/yıl gezinme ----
  const shiftMonth = (delta) => {
    setView((v) => {
      const nd = new Date(v.year, v.month + delta, 1);
      return { year: nd.getFullYear(), month: nd.getMonth() };
    });
  };
  const goToday = () =>
    setView({ year: today.getFullYear(), month: today.getMonth() });

  // ---- Resmi / Dini bayramları takvime uygula ----
  const applyResmi = () => {
    setDays((prev) => {
      const next = { ...prev };
      resmiTatiller(view.year).forEach((h) => {
        next[h.tarih] = {
          type: "tam",
          category: "resmi",
          name: h.name,
          description: next[h.tarih]?.description || "",
        };
      });
      return next;
    });
  };

  const applyDini = () => {
    const list = diniBayramlar(view.year);
    if (list.length === 0) {
      alert(`${view.year} yılı için dini bayram tarihleri tabloda tanımlı değil. Günleri takvimden elle ekleyebilirsiniz.`);
      return;
    }
    setDays((prev) => {
      const next = { ...prev };
      list.forEach((h) => {
        next[h.tarih] = {
          type: h.type,
          category: "dini",
          name: h.name,
          description: next[h.tarih]?.description || "",
        };
      });
      return next;
    });
  };

  // ---- Modal ----
  const openDay = (date) => {
    if (!date) return;
    const iso = isoOf(date);
    const info = resolveDay(date);
    setSelected({ iso, date });
    setEditType(info.type);
    setEditName(info.name || "");
    setEditDesc(info.description || "");
    setEditCategory(info.category === "resmi" || info.category === "dini" ? info.category : "ozel");
  };

  const closeModal = () => setSelected(null);

  const saveDay = () => {
    if (!selected) return;
    setDays((prev) => ({
      ...prev,
      [selected.iso]: {
        type: editType,
        category: editCategory,
        name: editName.trim(),
        description: editDesc.trim(),
      },
    }));
    closeModal();
  };

  const deleteDay = () => {
    if (!selected) return;
    setDays((prev) => {
      const next = { ...prev };
      delete next[selected.iso];
      return next;
    });
    closeModal();
  };

  // ---- Kaydet (kalıcı) ----
  const handleSave = async () => {
    setSaving(true);
    try {
      const daysArr = Object.entries(days).map(([tarih, v]) => ({
        tarih,
        type: v.type,
        category: v.category,
        name: v.name,
        description: v.description,
      }));

      const res = await fetch(`${API}/api/takvim-gunler`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          isHolidayChecked,
          saturdayType: TO_LEGACY[weekend.saturday] || weekend.saturday,
          sundayType: TO_LEGACY[weekend.sunday] || weekend.sunday,
          days: daysArr,
        }),
      });

      if (!res.ok) {
        const detay = await res.text().catch(() => "");
        throw new Error(`${res.status} ${detay.slice(0, 200)}`);
      }
      alert("Takvim ayarları kaydedildi!");
    } catch (err) {
      console.error("Kaydetme hatası:", err);
      alert(`Ayarlar kaydedilirken bir hata oluştu.\n${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ---- Hafta sonu radyo grubu ----
  const weekendRadios = (key, current) => (
    <div className="cal-radio-group">
      {TYPE_OPTIONS.map((opt) => (
        <label key={opt.value} className="cal-radio">
          <input
            type="radio"
            name={`weekend-${key}`}
            value={opt.value}
            checked={current === opt.value}
            onChange={() => setWeekend((w) => ({ ...w, [key]: opt.value }))}
            className="toggle-input"
          />
          <span className="toggle-card">{opt.label}</span>
        </label>
      ))}
    </div>
  );

  const selectedInfo = selected ? resolveDay(selected.date) : null;

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Takvim Yönetimi</h1>
          <p className="page__subtitle">Hafta sonu kuralları, resmi ve dini tatiller</p>
        </div>
      </div>

      <div className="card">
        {/* Hafta sonu ayarları */}
        <div className="card__body">
          <h2 className="section-title">Hafta Sonu Ayarları</h2>
          <div className="cal-weekend-grid">
            <div>
              <label className="form-label u-mb-2">Cumartesi</label>
              {weekendRadios("saturday", weekend.saturday)}
            </div>
            <div>
              <label className="form-label u-mb-2">Pazar</label>
              {weekendRadios("sunday", weekend.sunday)}
            </div>
          </div>
        </div>

        {/* Bayram butonları */}
        <div className="card__body">
          <h2 className="section-title">
            Resmi ve Dini Tatiller ({view.year})
          </h2>
          <div className="btn-group">
            <button type="button" onClick={applyResmi} className="btn btn--info">
              Resmi Bayramları Göster
            </button>
            <button type="button" onClick={applyDini} className="btn btn--primary">
              Dini Bayramları Göster
            </button>
          </div>
          <p className="form-helper u-mt-2">
            Eklenen tatilleri takvimden herhangi bir güne tıklayarak düzenleyebilir veya silebilirsiniz.
          </p>
        </div>

        {/* Takvim başlık + gezinme */}
        <div className="card__body">
          <div className="cal-toolbar">
            <button className="btn btn--ghost btn--sm" onClick={() => shiftMonth(-1)}>‹ Önceki Ay</button>
            <div className="cal-toolbar__title">
              {AY_ADLARI[view.month]} {view.year}
            </div>
            <div className="btn-group">
              <button className="btn btn--ghost btn--sm" onClick={goToday}>Bugün</button>
              <button className="btn btn--ghost btn--sm" onClick={() => shiftMonth(1)}>Sonraki Ay ›</button>
            </div>
          </div>

          {/* Renk lejantı */}
          <div className="cal-legend">
            <span className="cal-legend__item"><i className="cal-dot cal-dot--calisma" />Çalışma</span>
            <span className="cal-legend__item"><i className="cal-dot cal-dot--yarim" />Yarım Gün</span>
            <span className="cal-legend__item"><i className="cal-dot cal-dot--tam" />Tam Gün Tatil</span>
            <span className="cal-legend__item"><i className="cal-dot cal-dot--resmi" />Resmi Tatil</span>
            <span className="cal-legend__item"><i className="cal-dot cal-dot--dini" />Dini Bayram</span>
            <span className="cal-legend__item"><i className="cal-dot cal-dot--ozel" />Kurum Özel</span>
          </div>

          {loading ? (
            <p className="u-text-center" style={{ color: "var(--color-text-muted)" }}>Yükleniyor…</p>
          ) : (
            <div className="cal-month">
              <div className="cal-weekdays">
                {HAFTA_BASLIK.map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="cal-days">
                {monthGrid.map((date, i) => {
                  if (!date) return <div key={i} className="cal-cell cal-cell--empty" />;
                  const info = resolveDay(date);
                  const iso = isoOf(date);
                  const isToday = iso === todayIso;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={cellClass(info, isToday)}
                      onClick={() => openDay(date)}
                      title={info.name || TYPE_LABEL[info.type]}
                    >
                      <span className="cal-cell__num">{date.getDate()}</span>
                      {info.name && <span className="cal-cell__label">{info.name}</span>}
                      {info.description && <i className="cal-cell__note" title={info.description} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="card__body">
          <button onClick={handleSave} disabled={saving} className="btn btn--primary btn--block btn--lg">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>

      {/* Gün detay modalı */}
      {selected && selectedInfo && (
        <div className="cal-modal-overlay" onClick={closeModal}>
          <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cal-modal__header">
              <h3 className="cal-modal__title">Gün Detayı</h3>
              <button className="cal-modal__close" onClick={closeModal} aria-label="Kapat">×</button>
            </div>

            <div className="cal-modal__body">
              <dl className="cal-detail">
                <div className="cal-detail__row">
                  <dt>Tarih</dt>
                  <dd>{selected.date.toLocaleDateString("tr-TR")}</dd>
                </div>
                <div className="cal-detail__row">
                  <dt>Gün Adı</dt>
                  <dd>{GUN_ADLARI[selected.date.getDay()]}</dd>
                </div>
                <div className="cal-detail__row">
                  <dt>Tatil Türü</dt>
                  <dd>
                    <span className="badge badge--info">{TYPE_LABEL[selectedInfo.type]}</span>{" "}
                    <span className="badge">{CATEGORY_LABEL[selectedInfo.category]}</span>
                  </dd>
                </div>
                {selectedInfo.name && (
                  <div className="cal-detail__row">
                    <dt>Ad</dt>
                    <dd>{selectedInfo.name}</dd>
                  </div>
                )}
                {selectedInfo.description && (
                  <div className="cal-detail__row">
                    <dt>Açıklama</dt>
                    <dd>{selectedInfo.description}</dd>
                  </div>
                )}
              </dl>

              <hr className="cal-modal__divider" />

              <h4 className="u-mb-2" style={{ fontSize: "var(--fs-sm)", color: "var(--color-text-soft)" }}>
                Düzenle
              </h4>

              <label className="form-label u-mb-2">Gün Türü</label>
              <div className="cal-radio-group u-mb-4">
                {TYPE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="cal-radio">
                    <input
                      type="radio"
                      name="edit-type"
                      value={opt.value}
                      checked={editType === opt.value}
                      onChange={() => setEditType(opt.value)}
                      className="toggle-input"
                    />
                    <span className="toggle-card">{opt.label}</span>
                  </label>
                ))}
              </div>

              <div className="form-field u-mb-4">
                <label className="form-label">Ad (opsiyonel)</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="form-control"
                  placeholder="Örn. Kurban Bayramı 1. Gün"
                />
              </div>

              <div className="form-field">
                <label className="form-label">Açıklama</label>
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  className="form-control"
                  rows={3}
                  placeholder="Örn. Bayram sonrası kurum tarafından ek tatil ilan edildi."
                />
              </div>
            </div>

            <div className="cal-modal__footer">
              <button className="btn btn--success" onClick={saveDay}>Kaydet</button>
              {selectedInfo.isOverride && (
                <button className="btn btn--danger" onClick={deleteDay}>Sil</button>
              )}
              <button className="btn btn--ghost" onClick={closeModal}>Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TarihSec;
